import { useState } from "react";
import { toast } from "sonner";
import { Copy, Phone, MessageCircle, Eye, Download, Upload, Trash2, Loader2 } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { APP_STATUSES, CARD, DOC_KINDS, Badge, Section, copyText, download, fmtDay, expirySoon } from "@/admin/hr/common";
import { NotesPanel, ReminderPanel, MailPanel, DeletePanel } from "@/admin/hr/Panels";
import { DocViewer, docUrl } from "@/admin/hr/DocViewer";

const INFO = [["tc_masked", "T.C. Kimlik No"], ["phone", "Telefon"], ["email", "E-posta"], ["birth_date", "Doğum Tarihi"], ["city", "İl"], ["district", "İlçe"], ["card_type", "Kimlik Kartı"], ["card_number", "Kart Numarası"], ["card_expiry", "Kart Bitiş Tarihi"], ["regions", "Çalışabileceği Bölgeler"], ["experience", "Mesleki Deneyim"], ["description", "Başvuru Açıklaması"], ["source", "Kaynak"]];
const WIDE = ["experience", "description", "regions"];

function TcReveal({ a }) {
  const [tc, setTc] = useState("");
  if (!a.tc_masked) return "-";
  const show = async () => {
    try {
      setTc((await api.get(`/admin/hr/applications/${a.id}/tc`)).data.tc_no);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <span className="inline-flex items-center gap-2">
      <span className="font-mono" data-testid="detail-tc-value">{tc || a.tc_masked}</span>
      {!tc && <button type="button" onClick={show} className="text-xs font-semibold text-[color:var(--red)] underline" data-testid="detail-tc-reveal">Göster</button>}
    </span>
  );
}

function birthText(a) {
  if (a.birth_date) return fmtDay(a.birth_date);
  return a.birth_year ? `${a.birth_year} (yıl)` : "-";
}

function expiryText(d) {
  if (!d) return "-";
  return expirySoon(d) ? `${fmtDay(d)} (süresi yaklaşıyor veya geçti)` : fmtDay(d);
}

const VALUE_RENDER = {
  tc_masked: (a) => <TcReveal a={a} />,
  card_type: (a) => CARD[a.card_type] || "-",
  birth_date: birthText,
  card_expiry: (a) => expiryText(a.card_expiry),
};

function val(k, a) {
  const render = VALUE_RENDER[k];
  return render ? render(a) : a[k] || "-";
}

function DocRow({ app, d, onView, onRemove }) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm" data-testid={`doc-row-${d.kind}`}>
      <span><b>{DOC_KINDS[d.kind]}</b> <span className="text-xs text-zinc-500">{d.filename} · {(d.size / 1024).toFixed(0)} KB</span></span>
      <span className="flex gap-1">
        <button onClick={() => onView(d)} className="rounded p-1.5 hover:bg-zinc-100" aria-label="Görüntüle" data-testid={`doc-view-${d.id}`}><Eye className="h-4 w-4" /></button>
        <button onClick={() => download(docUrl(app.id, d), d.filename, { params: { download: 1 } })} className="rounded p-1.5 hover:bg-zinc-100" aria-label="İndir" data-testid={`doc-download-${d.id}`}><Download className="h-4 w-4" /></button>
        <button onClick={() => onRemove(d)} className="rounded p-1.5 text-red-600 hover:bg-red-50" aria-label="Sil" data-testid={`doc-delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>
      </span>
    </li>
  );
}

function UploadBar({ app, onSaved }) {
  const [kind, setKind] = useState("other");
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file);
    setBusy(true);
    try {
      onSaved((await api.post(`/admin/hr/applications/${app.id}/documents`, fd)).data);
      toast.success("Belge yüklendi");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-2 sm:flex-row">
      <select value={kind} onChange={(e) => setKind(e.target.value)} className="field-input sm:w-56" data-testid="doc-upload-kind">
        {Object.entries(DOC_KINDS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <label className="btn-outline cursor-pointer !py-2">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Belge ekle
        <input type="file" accept=".jpg,.jpeg,.png,.pdf,.doc,.docx" className="sr-only" onChange={(e) => { if (e.target.files?.[0]) upload(e.target.files[0]); e.target.value = ""; }} data-testid="doc-upload-input" />
      </label>
    </div>
  );
}

function DocsPanel({ app, onSaved }) {
  const [view, setView] = useState(null);
  const remove = async (d) => {
    if (!window.confirm(`${DOC_KINDS[d.kind]} silinsin mi?`)) return;
    try {
      onSaved((await api.delete(`/admin/hr/applications/${app.id}/documents/${d.id}`)).data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Section title="Belgeler" testid="docs-panel" action={<Badge value={app.doc_status} testid="detail-doc-status" />}>
      {app.missing_docs?.length > 0 && <p className="mb-2 text-xs text-orange-700">Eksik: {app.missing_docs.join(", ")}</p>}
      <ul className="mb-3 divide-y divide-zinc-100">
        {(app.documents || []).map((d) => <DocRow key={d.id} app={app} d={d} onView={setView} onRemove={remove} />)}
        {!app.documents?.length && <li className="py-2 text-sm text-zinc-500">Belge yüklenmemiş.</li>}
      </ul>
      <UploadBar app={app} onSaved={onSaved} />
      <DocViewer appId={app.id} doc={view} onClose={() => setView(null)} />
    </Section>
  );
}

const jobMeta = (j) => [j.organization, j.date && fmtDay(j.date), j.location].filter(Boolean).join(" · ");

function JobBox({ a }) {
  return (
    <div className="rounded-lg border-l-4 border-[color:var(--red)] bg-red-50 p-3 text-sm" data-testid="detail-job">
      <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">Başvurulan Görev / İlan</p>
      <p className="font-bold text-zinc-950">{a.job?.title || a.position || "-"}</p>
      {a.job && <p className="text-xs text-zinc-600">{jobMeta(a.job)}</p>}
    </div>
  );
}

function ContactButtons({ phone }) {
  const wa = (phone || "").replace(/\D/g, "").replace(/^0/, "90");
  return (
    <div className="flex flex-wrap gap-2">
      <a href={`tel:${phone}`} className="btn-outline !py-2" data-testid="detail-call-btn"><Phone className="h-4 w-4" /> Ara</a>
      <button onClick={() => copyText(phone)} className="btn-outline !py-2" data-testid="detail-copy-phone"><Copy className="h-4 w-4" /> Telefonu Kopyala</button>
      <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" className="btn-outline !py-2"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
    </div>
  );
}

function StatusFields({ a, patch }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div>
        <label className="field-label">Başvuru durumu</label>
        <select value={a.status} onChange={(e) => patch({ status: e.target.value })} className="field-input" data-testid="detail-status-select">
          {APP_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <label className="field-label">Son iletişim tarihi</label>
        <input type="date" value={a.last_contact_at || ""} onChange={(e) => patch({ last_contact_at: e.target.value })} className="field-input" data-testid="detail-last-contact" />
      </div>
    </div>
  );
}

function InfoList({ a }) {
  return (
    <dl className="grid gap-x-6 gap-y-3 rounded-lg border border-zinc-200 p-4 text-sm sm:grid-cols-2">
      {INFO.map(([k, l]) => (
        <div key={k} className={WIDE.includes(k) ? "sm:col-span-2" : ""}>
          <dt className="text-xs font-bold uppercase tracking-wider text-zinc-500">{l}</dt>
          <dd className="mt-0.5 whitespace-pre-wrap break-words text-zinc-900" data-testid={`detail-${k}`}>{val(k, a)}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function ApplicationDetail({ item, onChanged, onClose }) {
  const [a, setA] = useState(item);
  const saved = (d) => { setA(d); onChanged(); };
  const patch = async (body) => {
    try {
      saved((await api.patch(`/admin/hr/applications/${a.id}`, body)).data);
      toast.success("Kaydedildi");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const reload = async () => saved((await api.get(`/admin/hr/applications/${a.id}`)).data);
  return (
    <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="application-detail">
      <DialogHeader><DialogTitle>{a.name} <span className="ml-2 text-sm font-semibold text-[color:var(--red)]" data-testid="detail-app-no">{a.app_no}</span></DialogTitle></DialogHeader>
      <p className="text-xs text-zinc-500">Başvuru: {fmtDate(a.created_at)} · Bildirim: {a.email_status || "-"}</p>
      <JobBox a={a} />
      <ContactButtons phone={a.phone} />
      <StatusFields a={a} patch={patch} />
      <InfoList a={a} />
      <DocsPanel app={a} onSaved={saved} />
      <NotesPanel coll="applications" item={a} onSaved={saved} />
      <ReminderPanel coll="applications" item={a} />
      <MailPanel coll="applications" item={a} onSent={reload} />
      <DeletePanel coll="applications" item={a} no={a.app_no} onDeleted={() => { onClose(); onChanged(); }} />
    </DialogContent>
  );
}
